import { useState } from 'react';
import styles from './ColorsPage.module.css';

export const ColorsPage = () => {
    const [copiedColor, setCopiedColor] = useState<string | null>(null);

    const copyColor = (color: string, name: string) => {
        navigator.clipboard.writeText(color);
        setCopiedColor(name);
        setTimeout(() => setCopiedColor(null), 2000);
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h1>Colors</h1>
                <p className={styles.description}>
                    Paleta de colores del sistema Zafiro ERP. Todos los colores están definidos
                    como CSS Variables para fácil mantenimiento y soporte de temas.
                </p>
            </div>

            {/* Colores Principales */}
            <section className={styles.section}>
                <h2>Colores Principales (Brand)</h2>
                <p>Colores primarios y secundarios del brand Zafiro ERP.</p>

                <div className={styles.colorGrid}>
                    <div
                        className={styles.colorCard}
                        onClick={() => copyColor('var(--color-primary)', 'Primary')}
                    >
                        <div className={styles.colorSwatch} style={{ background: 'var(--color-primary)' }} />
                        <div className={styles.colorInfo}>
                            <div className={styles.colorName}>Primary</div>
                            <code className={styles.colorVar}>--color-primary</code>
                            <div className={styles.hexValue}>#3D9D93</div>
                            {copiedColor === 'Primary' && <span className={styles.copied}>✓ Copiado</span>}
                        </div>
                    </div>

                    <div
                        className={styles.colorCard}
                        onClick={() => copyColor('var(--color-primary-dark)', 'Primary Dark')}
                    >
                        <div className={styles.colorSwatch} style={{ background: 'var(--color-primary-dark)' }} />
                        <div className={styles.colorInfo}>
                            <div className={styles.colorName}>Primary Dark</div>
                            <code className={styles.colorVar}>--color-primary-dark</code>
                            <div className={styles.hexValue}>#2D7D73</div>
                            {copiedColor === 'Primary Dark' && <span className={styles.copied}>✓ Copiado</span>}
                        </div>
                    </div>

                    <div
                        className={styles.colorCard}
                        onClick={() => copyColor('var(--color-primary-light)', 'Primary Light')}
                    >
                        <div className={styles.colorSwatch} style={{ background: 'var(--color-primary-light)' }} />
                        <div className={styles.colorInfo}>
                            <div className={styles.colorName}>Primary Light</div>
                            <code className={styles.colorVar}>--color-primary-light</code>
                            <div className={styles.hexValue}>#4AAD9F</div>
                            {copiedColor === 'Primary Light' && <span className={styles.copied}>✓ Copiado</span>}
                        </div>
                    </div>

                    <div
                        className={styles.colorCard}
                        onClick={() => copyColor('var(--color-secondary)', 'Secondary')}
                    >
                        <div className={styles.colorSwatch} style={{ background: 'var(--color-secondary)' }} />
                        <div className={styles.colorInfo}>
                            <div className={styles.colorName}>Secondary</div>
                            <code className={styles.colorVar}>--color-secondary</code>
                            <div className={styles.hexValue}>#64748b</div>
                            {copiedColor === 'Secondary' && <span className={styles.copied}>✓ Copiado</span>}
                        </div>
                    </div>

                    <div
                        className={styles.colorCard}
                        onClick={() => copyColor('var(--color-secondary-dark)', 'Secondary Dark')}
                    >
                        <div className={styles.colorSwatch} style={{ background: 'var(--color-secondary-dark)' }} />
                        <div className={styles.colorInfo}>
                            <div className={styles.colorName}>Secondary Dark</div>
                            <code className={styles.colorVar}>--color-secondary-dark</code>
                            <div className={styles.hexValue}>#475569</div>
                            {copiedColor === 'Secondary Dark' && <span className={styles.copied}>✓ Copiado</span>}
                        </div>
                    </div>
                </div>
            </section>

            {/* Paleta Zafiro */}
            <section className={styles.section}>
                <h2>Paleta Zafiro Completa</h2>
                <p>Escala completa de 10 tonos de verde zafiro (50-900).</p>

                <div className={styles.paletteGrid}>
                    {[
                        { name: 'Zafiro 50', var: '--color-zafiro-50', hex: '#E0F2F1' },
                        { name: 'Zafiro 100', var: '--color-zafiro-100', hex: '#B2DFDB' },
                        { name: 'Zafiro 200', var: '--color-zafiro-200', hex: '#80CBC4' },
                        { name: 'Zafiro 300', var: '--color-zafiro-300', hex: '#4DB6AC' },
                        { name: 'Zafiro 400', var: '--color-zafiro-400', hex: '#3D9D93' },
                        { name: 'Zafiro 500', var: '--color-zafiro-500', hex: '#2D7D73' },
                        { name: 'Zafiro 600', var: '--color-zafiro-600', hex: '#267370' },
                        { name: 'Zafiro 700', var: '--color-zafiro-700', hex: '#1F6863' },
                        { name: 'Zafiro 800', var: '--color-zafiro-800', hex: '#185550' },
                        { name: 'Zafiro 900', var: '--color-zafiro-900', hex: '#0F3E3A' },
                    ].map((color) => (
                        <div
                            key={color.var}
                            className={styles.paletteCard}
                            onClick={() => copyColor(`var(${color.var})`, color.name)}
                        >
                            <div className={styles.paletteSwatch} style={{ background: color.hex }} />
                            <div className={styles.paletteInfo}>
                                <div className={styles.paletteName}>{color.name}</div>
                                <code className={styles.paletteVar}>{color.var}</code>
                                <div className={styles.hexValue}>{color.hex}</div>
                            </div>
                            {copiedColor === color.name && <span className={styles.copied}>✓ Copiado</span>}
                        </div>
                    ))}
                </div>
            </section>

            {/* Colores de Estado */}
            <section className={styles.section}>
                <h2>Colores de Estado</h2>
                <p>Colores para indicar estados: éxito, error, advertencia e información.</p>

                <div className={styles.colorGrid}>
                    <div
                        className={styles.colorCard}
                        onClick={() => copyColor('var(--color-success)', 'Success')}
                    >
                        <div className={styles.colorSwatch} style={{ background: 'var(--color-success)' }} />
                        <div className={styles.colorInfo}>
                            <div className={styles.colorName}>Success</div>
                            <code className={styles.colorVar}>--color-success</code>
                            {copiedColor === 'Success' && <span className={styles.copied}>✓ Copiado</span>}
                        </div>
                    </div>

                    <div
                        className={styles.colorCard}
                        onClick={() => copyColor('var(--color-success-dark)', 'Success Dark')}
                    >
                        <div className={styles.colorSwatch} style={{ background: 'var(--color-success-dark)' }} />
                        <div className={styles.colorInfo}>
                            <div className={styles.colorName}>Success Dark</div>
                            <code className={styles.colorVar}>--color-success-dark</code>
                            {copiedColor === 'Success Dark' && <span className={styles.copied}>✓ Copiado</span>}
                        </div>
                    </div>

                    <div
                        className={styles.colorCard}
                        onClick={() => copyColor('var(--color-error)', 'Error')}
                    >
                        <div className={styles.colorSwatch} style={{ background: 'var(--color-error)' }} />
                        <div className={styles.colorInfo}>
                            <div className={styles.colorName}>Error</div>
                            <code className={styles.colorVar}>--color-error</code>
                            {copiedColor === 'Error' && <span className={styles.copied}>✓ Copiado</span>}
                        </div>
                    </div>

                    <div
                        className={styles.colorCard}
                        onClick={() => copyColor('var(--color-error-dark)', 'Error Dark')}
                    >
                        <div className={styles.colorSwatch} style={{ background: 'var(--color-error-dark)' }} />
                        <div className={styles.colorInfo}>
                            <div className={styles.colorName}>Error Dark</div>
                            <code className={styles.colorVar}>--color-error-dark</code>
                            {copiedColor === 'Error Dark' && <span className={styles.copied}>✓ Copiado</span>}
                        </div>
                    </div>

                    <div
                        className={styles.colorCard}
                        onClick={() => copyColor('var(--color-warning)', 'Warning')}
                    >
                        <div className={styles.colorSwatch} style={{ background: 'var(--color-warning)' }} />
                        <div className={styles.colorInfo}>
                            <div className={styles.colorName}>Warning</div>
                            <code className={styles.colorVar}>--color-warning</code>
                            {copiedColor === 'Warning' && <span className={styles.copied}>✓ Copiado</span>}
                        </div>
                    </div>

                    <div
                        className={styles.colorCard}
                        onClick={() => copyColor('var(--color-info)', 'Info')}
                    >
                        <div className={styles.colorSwatch} style={{ background: 'var(--color-info)' }} />
                        <div className={styles.colorInfo}>
                            <div className={styles.colorName}>Info</div>
                            <code className={styles.colorVar}>--color-info</code>
                            {copiedColor === 'Info' && <span className={styles.copied}>✓ Copiado</span>}
                        </div>
                    </div>
                </div>
            </section>

            {/* Colores de Base */}
            <section className={styles.section}>
                <h2>Colores de Base</h2>
                <p>Colores básicos blanco y negro.</p>

                <div className={styles.colorGrid}>
                    <div
                        className={styles.colorCard}
                        onClick={() => copyColor('var(--color-white)', 'White')}
                    >
                        <div className={`${styles.colorSwatch} ${styles.bordered}`} style={{ background: 'var(--color-white)' }} />
                        <div className={styles.colorInfo}>
                            <div className={styles.colorName}>White</div>
                            <code className={styles.colorVar}>--color-white</code>
                            {copiedColor === 'White' && <span className={styles.copied}>✓ Copiado</span>}
                        </div>
                    </div>

                    <div
                        className={styles.colorCard}
                        onClick={() => copyColor('var(--color-black)', 'Black')}
                    >
                        <div className={styles.colorSwatch} style={{ background: 'var(--color-black)' }} />
                        <div className={styles.colorInfo}>
                            <div className={styles.colorName}>Black</div>
                            <code className={styles.colorVar}>--color-black</code>
                            {copiedColor === 'Black' && <span className={styles.copied}>✓ Copiado</span>}
                        </div>
                    </div>
                </div>
            </section>

            {/* Backgrounds */}
            <section className={styles.section}>
                <h2>Backgrounds</h2>
                <p>Colores de fondo para diferentes niveles de contenido.</p>

                <div className={styles.colorGrid}>
                    <div
                        className={styles.colorCard}
                        onClick={() => copyColor('var(--bg-primary)', 'Background Primary')}
                    >
                        <div className={`${styles.colorSwatch} ${styles.bordered}`} style={{ background: 'var(--bg-primary)' }} />
                        <div className={styles.colorInfo}>
                            <div className={styles.colorName}>BG Primary</div>
                            <code className={styles.colorVar}>--bg-primary</code>
                            <div className={styles.usage}>Fondo principal de la aplicación</div>
                            {copiedColor === 'Background Primary' && <span className={styles.copied}>✓ Copiado</span>}
                        </div>
                    </div>

                    <div
                        className={styles.colorCard}
                        onClick={() => copyColor('var(--bg-secondary)', 'Background Secondary')}
                    >
                        <div className={`${styles.colorSwatch} ${styles.bordered}`} style={{ background: 'var(--bg-secondary)' }} />
                        <div className={styles.colorInfo}>
                            <div className={styles.colorName}>BG Secondary</div>
                            <code className={styles.colorVar}>--bg-secondary</code>
                            <div className={styles.usage}>Cards, modales, elementos elevados</div>
                            {copiedColor === 'Background Secondary' && <span className={styles.copied}>✓ Copiado</span>}
                        </div>
                    </div>

                    <div
                        className={styles.colorCard}
                        onClick={() => copyColor('var(--bg-hover)', 'Background Hover')}
                    >
                        <div className={`${styles.colorSwatch} ${styles.bordered}`} style={{ background: 'var(--bg-hover)' }} />
                        <div className={styles.colorInfo}>
                            <div className={styles.colorName}>BG Hover</div>
                            <code className={styles.colorVar}>--bg-hover</code>
                            <div className={styles.usage}>Estado hover de filas, items</div>
                            {copiedColor === 'Background Hover' && <span className={styles.copied}>✓ Copiado</span>}
                        </div>
                    </div>
                </div>
            </section>

            {/* Textos */}
            <section className={styles.section}>
                <h2>Colores de Texto</h2>
                <p>Jerarquía de colores para textos.</p>

                <div className={styles.textExamples}>
                    <div className={styles.textExample}>
                        <p style={{ color: 'var(--text-primary)', fontSize: '1.2rem', fontWeight: 600 }}>
                            Texto Primary
                        </p>
                        <code className={styles.colorVar}>--text-primary</code>
                        <div className={styles.usage}>Títulos, encabezados, texto principal</div>
                    </div>

                    <div className={styles.textExample}>
                        <p style={{ color: 'var(--text-secondary)', fontSize: '1rem' }}>
                            Texto Secondary
                        </p>
                        <code className={styles.colorVar}>--text-secondary</code>
                        <div className={styles.usage}>Texto de soporte, descripciones</div>
                    </div>

                    <div className={styles.textExample}>
                        <p style={{ color: 'var(--text-tertiary)', fontSize: '0.9rem' }}>
                            Texto Tertiary
                        </p>
                        <code className={styles.colorVar}>--text-tertiary</code>
                        <div className={styles.usage}>Placeholders, texto de menor importancia</div>
                    </div>
                </div>
            </section>

            {/* Borders */}
            <section className={styles.section}>
                <h2>Borders</h2>
                <p>Colores para bordes y separadores.</p>

                <div className={styles.borderExamples}>
                    <div className={styles.borderExample} style={{ borderColor: 'var(--border-color)' }}>
                        <code>--border-color</code>
                        <span>Bordes normales</span>
                    </div>
                    <div className={styles.borderExample} style={{ borderColor: 'var(--border-color-focus)' }}>
                        <code>--border-color-focus</code>
                        <span>Bordes en focus</span>
                    </div>
                </div>
            </section>

            {/* Uso */}
            <section className={styles.section}>
                <h2>Cómo Usar</h2>
                <div className={styles.usageGuide}>
                    <h3>En CSS</h3>
                    <code className={styles.code}>
                        {`.myElement {
  background: var(--bg-primary);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
}

.myButton {
  background: var(--color-primary);
  color: var(--color-white);
}

.myButton:hover {
  background: var(--color-primary-dark);
}`}
                    </code>

                    <h3 style={{ marginTop: '2rem' }}>Inline en React</h3>
                    <code className={styles.code}>
                        {`<div style={{ 
  background: 'var(--bg-secondary)', 
  color: 'var(--text-primary)' 
}}>
  Contenido
</div>`}
                    </code>
                </div>
            </section>
        </div>
    );
};
